// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, InlineNotification, Select, SelectItem, TextInput } from '@carbon/react';
import { useProvisionOrganization, describeApiError } from '../../shared/provisioning';
import type { ProvisionOrganizationRequest } from '../../shared/provisioning';
import form from './forms.module.css';
import styles from './NewOrganizationPage.module.css';

// OMS `DomainKey`: letter-led, alphanumeric, single _ or - separators, max 64.
const ORG_KEY = /^[A-Za-z][A-Za-z0-9]*([_-][A-Za-z0-9]+)*$/;

const SEGMENTS = ['Hyperscale', 'Colocation', 'Enterprise', 'Edge'];
const CONTACT_TYPES = ['business', 'operational', 'finance'];

const EMPTY = {
  organizationKey: '',
  preferredName: '',
  legalEntityName: '',
  timeZone: '',
  primaryBusinessSegment: SEGMENTS[0],
  url: '',
  addressLine1: '',
  city: '',
  postalCode: '',
  country: '',
  contactName: '',
  contactEmail: '',
  contactType: CONTACT_TYPES[0],
};

type Fields = typeof EMPTY;

export default function NewOrganizationPage() {
  const navigate = useNavigate();
  const [f, setF] = useState<Fields>(EMPTY);
  const [submitted, setSubmitted] = useState(false);
  const provision = useProvisionOrganization();

  const set = (k: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF(prev => ({ ...prev, [k]: e.target.value }));

  // OMS requires every one of these; the columns are nullable but the create
  // contract is not, so an omission is a 422 rather than a partial record.
  // Validate the trimmed values; the payload sends the same trimmed values so
  // what passes validation is exactly what reaches OMS/IAM.
  const v = (k: keyof Fields) => f[k].trim();
  const errors: Partial<Record<keyof Fields, string>> = {};
  if (!v('organizationKey')) errors.organizationKey = 'Required';
  else if (v('organizationKey').length > 64) errors.organizationKey = 'At most 64 characters';
  else if (!ORG_KEY.test(v('organizationKey')))
    errors.organizationKey = 'Letters, digits, single - or _ separators';
  if (!v('preferredName')) errors.preferredName = 'Required';
  if (!v('legalEntityName')) errors.legalEntityName = 'Required';
  if (!v('timeZone')) errors.timeZone = 'Required';
  if (!v('url')) errors.url = 'Required';
  if (!v('addressLine1')) errors.addressLine1 = 'Required';
  if (!v('city')) errors.city = 'Required';
  if (!v('postalCode')) errors.postalCode = 'Required';
  if (!/^[A-Za-z]{2}$/.test(v('country'))) errors.country = 'Two-letter ISO code';
  if (!v('contactName')) errors.contactName = 'Required';
  if (!/^\S+@\S+\.\S+$/.test(v('contactEmail'))) errors.contactEmail = 'Valid email required';

  const invalid = (k: keyof Fields) => submitted && !!errors[k];
  const msg = (k: keyof Fields) => (submitted ? errors[k] : undefined);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (Object.keys(errors).length > 0) return;

    const body: ProvisionOrganizationRequest = {
      organizationKey: v('organizationKey'),
      preferredName: v('preferredName'),
      legalEntityName: v('legalEntityName'),
      timeZone: v('timeZone'),
      primaryBusinessSegment: f.primaryBusinessSegment,
      url: v('url'),
      address: {
        addressLine1: v('addressLine1'),
        city: v('city'),
        postalCode: v('postalCode'),
        country: v('country').toUpperCase(),
      },
      contacts: [{ name: v('contactName'), email: v('contactEmail'), contactType: f.contactType }],
    };
    provision.mutate(body, { onSuccess: run => navigate(`/runs/${run.runId}`) });
  };

  return (
    <>
      <div className={styles.head}>
        <h1 className={styles.title}>New organization</h1>
      </div>

      {provision.isError && (
        <div className={form.notice}>
          <InlineNotification
            kind="error"
            lowContrast
            title={describeApiError(provision.error, { action: 'create an organization' }).title}
            subtitle={
              describeApiError(provision.error, { action: 'create an organization' }).message
            }
            hideCloseButton
          />
        </div>
      )}

      {/* autoComplete off: these fields describe the customer organization,
          not the signed-in operator - a browser autofill of the operator's own
          address/email here would flow verbatim into the customer record. */}
      <form className={form.form} onSubmit={onSubmit} noValidate autoComplete="off">
        <div className={form.group}>
          <div className={form.groupTitle}>Identity</div>
          <div className={form.row}>
            <TextInput
              id="organizationKey"
              labelText="Organization key"
              value={f.organizationKey}
              onChange={set('organizationKey')}
              invalid={invalid('organizationKey')}
              invalidText={msg('organizationKey')}
            />
            <TextInput
              id="preferredName"
              labelText="Name"
              value={f.preferredName}
              onChange={set('preferredName')}
              invalid={invalid('preferredName')}
              invalidText={msg('preferredName')}
            />
          </div>
          <div className={form.row}>
            <TextInput
              id="legalEntityName"
              labelText="Legal entity name"
              value={f.legalEntityName}
              onChange={set('legalEntityName')}
              invalid={invalid('legalEntityName')}
              invalidText={msg('legalEntityName')}
            />
            <Select
              id="primaryBusinessSegment"
              labelText="Business segment"
              value={f.primaryBusinessSegment}
              onChange={set('primaryBusinessSegment')}
            >
              {SEGMENTS.map(s => (
                <SelectItem key={s} value={s} text={s} />
              ))}
            </Select>
          </div>
          <div className={form.row}>
            <TextInput
              id="timeZone"
              labelText="Time zone"
              placeholder="America/Chicago"
              value={f.timeZone}
              onChange={set('timeZone')}
              invalid={invalid('timeZone')}
              invalidText={msg('timeZone')}
            />
            <TextInput
              id="url"
              labelText="URL"
              placeholder="https://example.com"
              value={f.url}
              onChange={set('url')}
              invalid={invalid('url')}
              invalidText={msg('url')}
            />
          </div>
        </div>

        <div className={form.group}>
          <div className={form.groupTitle}>Legal address</div>
          <div className={form.field}>
            <TextInput
              id="addressLine1"
              labelText="Address"
              value={f.addressLine1}
              onChange={set('addressLine1')}
              invalid={invalid('addressLine1')}
              invalidText={msg('addressLine1')}
            />
          </div>
          <div className={form.row}>
            <TextInput
              id="city"
              labelText="City"
              value={f.city}
              onChange={set('city')}
              invalid={invalid('city')}
              invalidText={msg('city')}
            />
            <TextInput
              id="postalCode"
              labelText="Postal code"
              value={f.postalCode}
              onChange={set('postalCode')}
              invalid={invalid('postalCode')}
              invalidText={msg('postalCode')}
            />
          </div>
          <div className={form.row}>
            <TextInput
              id="country"
              labelText="Country"
              placeholder="US"
              value={f.country}
              onChange={set('country')}
              invalid={invalid('country')}
              invalidText={msg('country')}
            />
          </div>
        </div>

        <div className={form.group}>
          <div className={form.groupTitle}>Primary contact</div>
          <div className={form.row}>
            <TextInput
              id="contactName"
              labelText="Contact name"
              value={f.contactName}
              onChange={set('contactName')}
              invalid={invalid('contactName')}
              invalidText={msg('contactName')}
            />
            <TextInput
              id="contactEmail"
              labelText="Email"
              value={f.contactEmail}
              onChange={set('contactEmail')}
              invalid={invalid('contactEmail')}
              invalidText={msg('contactEmail')}
            />
          </div>
          <div className={form.row}>
            <Select
              id="contactType"
              labelText="Contact type"
              value={f.contactType}
              onChange={set('contactType')}
            >
              {CONTACT_TYPES.map(c => (
                <SelectItem key={c} value={c} text={c} />
              ))}
            </Select>
          </div>
        </div>

        <div className={form.actions}>
          <Button type="submit" disabled={provision.isPending}>
            {provision.isPending ? 'Starting…' : 'Create organization'}
          </Button>
          <Button kind="ghost" onClick={() => navigate('/organizations')}>
            Cancel
          </Button>
        </div>
      </form>
    </>
  );
}
